import { useBulkPutDnaApi, usePutDnaApi } from 'api/dna'
import { Flex } from 'components/shared/flex'
import { Attribute, DateField, InputField, ReagentItemSelectField } from 'components/shared/input'
import { TableCell } from 'components/shared/widget'
import { GlobalStateContext } from 'contexts/global_state_context'
import { MasterDataContext } from 'contexts/master_data_context'
import { Dna, DnaForm } from 'entities/dna'
import lodash from 'lodash'
import { useContext } from 'react'
import { Button, Modal, Table } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm, usePrevious } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'

type EditDnaFormProps = {
    dna: Dna
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditDnaForm = (props: EditDnaFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutDnaApi()
    const form = useForm<DnaForm>(props.dna)
    const masterData = useContext(MasterDataContext)

    useEffectSkipFirst(() => {
        form.set(props.dna)
    }, [props.dna])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>Dnaの更新</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {form.object.id && (
                    <>
                        <h3 style={{ marginBottom: 10 }}>cDNA_ID: {props.dna.code}</h3>
                        <Flex>
                            <DateField required apiError={api.apiError} form={form} attr="date" label="Date" />
                        </Flex>
                        <div style={{ marginBottom: 20 }}>
                            <Flex>
                                <ReagentItemSelectField
                                    apiError={api.apiError}
                                    form={form}
                                    label="Library lot"
                                    attr="libraryReagentItemId"
                                    reagentKind="library"
                                />

                                <ReagentItemSelectField
                                    apiError={api.apiError}
                                    form={form}
                                    label="RT Spike in"
                                    attr="rtSpikeInReagentItemId"
                                    reagentKind="rt_spike_in"
                                />

                                <ReagentItemSelectField
                                    apiError={api.apiError}
                                    form={form}
                                    label="Free water"
                                    attr="dnaFreeWaterReagentItemId"
                                    reagentKind="free_water"
                                />
                            </Flex>
                            <Flex>
                                <InputField apiError={api.apiError} attr="errorNote" form={form} label="エラー" as="textarea" />
                            </Flex>
                            <Flex>
                                <InputField apiError={api.apiError} attr="libraryNote" form={form} label="Library Note" as="textarea" />
                            </Flex>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

type BulkEditDnaFormProps = {
    dnaList: Dna[]
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const BulkEditDnaForm = (props: BulkEditDnaFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = useBulkPutDnaApi()
    const form = useForm<DnaForm[]>(props.dnaList)
    const masterData = useContext(MasterDataContext)
    const allInputForm = useForm<DnaForm>({})
    const previousAllInputForm = usePrevious<DnaForm>(allInputForm.object)

    useEffectSkipFirst(() => {
        form.set(props.dnaList)
    }, [props.dnaList])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    const bulkInput = (attr: keyof DnaForm) => {
        const copy = [...form.object]
        copy.forEach((dna: DnaForm) => {
            const value: any = allInputForm.object[attr] as any
            dna[attr] = value
        })
        form.set(copy)
    }

    useEffectSkipFirst(() => {
        const diff: DnaForm = lodash.omitBy(allInputForm.object, (v, k) => previousAllInputForm[k as keyof DnaForm] === v)
        Object.keys(diff).forEach((k: string) => {
            bulkInput(k as keyof DnaForm)
        })
    }, [allInputForm.object])

    const handleClose = () => {
        api.clearApiError()
        props.onClose()
    }

    const formRow = <T extends unknown>(form: Form<T>, apiError?: ApiError, dna?: Dna, index?: number): JSX.Element => {
        const getAttr = (attr: string): Attribute<T> => {
            if (typeof index === 'number') {
                return [index, attr]
            } else {
                return attr as keyof T
            }
        }

        return (
            <>
                <th className="sticky-table-cell" style={{ backgroundColor: dna?.code ? 'white' : '#eee' }}>
                    {dna?.code ? dna.code : '一括入力'}
                </th>
                <td>
                    <DateField required apiError={apiError} form={form} attr={getAttr('date')} index={index} />
                </td>
                <td>
                    <ReagentItemSelectField apiError={api.apiError} form={form} attr={getAttr('libraryReagentItemId')} reagentKind="library" />
                </td>
                <td>
                    <ReagentItemSelectField apiError={api.apiError} form={form} attr={getAttr('rtSpikeInReagentItemId')} reagentKind="rt_spike_in" />
                </td>
                <td>
                    <ReagentItemSelectField apiError={api.apiError} form={form} attr={getAttr('dnaFreeWaterReagentItemId')} reagentKind="free_water" />
                </td>
                <td>
                    <InputField apiError={apiError} attr={getAttr('errorNote')} form={form} />
                </td>
                <td>
                    <InputField apiError={apiError} attr={getAttr('libraryNote')} form={form} />
                </td>
            </>
        )
    }

    return (
        <Modal show={props.show} onHide={handleClose} size="xl" fullscreen>
            <Modal.Header closeButton>
                <Modal.Title>cDnaの一括編集</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: 0 }}>
                {form.object.length > 0 && (
                    <div>
                        <Table className="sticky-table">
                            <thead>
                                <tr>
                                    <TableCell className="sticky-table-cell" th minWidth={240}>
                                        cDnaID
                                    </TableCell>
                                    <TableCell th minWidth={150}>
                                        Date
                                    </TableCell>
                                    <TableCell th minWidth={140}>
                                        Library lot
                                    </TableCell>
                                    <TableCell th minWidth={140}>
                                        Spike-in
                                    </TableCell>
                                    <TableCell th minWidth={140}>
                                        Free water
                                    </TableCell>
                                    <TableCell th width={300}>
                                        エラー
                                    </TableCell>
                                    <TableCell th minWidth={300}>
                                        Library Note
                                    </TableCell>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    className="vertical-align-middle-row"
                                    style={{
                                        borderBottomStyle: 'double',
                                        borderBottomWidth: '6px',
                                        background: '#eee',
                                    }}
                                >
                                    {formRow(allInputForm)}
                                </tr>
                                {props.dnaList.map((dna: Dna, index: number) => {
                                    return (
                                        <tr key={`bulk-update-dna-${dna.id}`} className="vertical-align-middle-row">
                                            {formRow(form, api.apiError, dna, index)}
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </Table>
                        <Flex></Flex>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
